import { type ComponentType, lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router';
import { AuthGate } from '@/components/layout/AuthGate';
import { RequirePermission } from '@/components/layout/RequirePermission';
import { importOrReload } from '@/lib/chunk-reload';
import NotFoundPage from '@/pages/NotFoundPage';
import TrackerPage from '@/pages/TrackerPage';

/**
 * The landing page (Time Tracker) is in the main bundle; every other page is its own chunk, loaded
 * when first visited (AppShell shows a fallback meanwhile). Reports alone brings in recharts, and
 * Import its parsers, so they no longer delay the first screen. A chunk missing after a deploy
 * reloads the page once (see chunk-reload.ts).
 */
const page = (load: () => Promise<{ default: ComponentType }>) => lazy(() => importOrReload(load));

const TimesheetPage = page(() => import('@/pages/TimesheetPage'));
const ReportsPage = page(() => import('@/pages/ReportsPage'));
const ProjectsPage = page(() => import('@/pages/ProjectsPage'));
const TeamPage = page(() => import('@/pages/TeamPage'));
const ClientsPage = page(() => import('@/pages/ClientsPage'));
const TagsPage = page(() => import('@/pages/TagsPage'));
const TimeOffPage = page(() => import('@/pages/TimeOffPage'));
const SettingsPage = page(() => import('@/pages/SettingsPage'));
const ImportPage = page(() => import('@/pages/ImportPage'));

export const routes: RouteObject[] = [
  {
    element: <AuthGate />,
    children: [
      { index: true, element: <Navigate to="/tracker" replace /> },
      { path: 'tracker', element: <TrackerPage /> },
      { path: 'timesheet', element: <TimesheetPage /> },
      { path: 'reports/*', element: <ReportsPage /> },
      { path: 'projects/*', element: <ProjectsPage /> },
      {
        path: 'team',
        element: (
          <RequirePermission permission="reports:all">
            <TeamPage />
          </RequirePermission>
        ),
      },
      { path: 'clients', element: <ClientsPage /> },
      { path: 'tags', element: <TagsPage /> },
      { path: 'time-off/*', element: <TimeOffPage /> },
      {
        path: 'settings',
        element: (
          <RequirePermission permission="settings:manage">
            <SettingsPage />
          </RequirePermission>
        ),
      },
      {
        path: 'import',
        element: (
          <RequirePermission permission="import:run">
            <ImportPage />
          </RequirePermission>
        ),
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];
